import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { Flex, Box, Button, Text } from '@chakra-ui/react';
import YouTube from 'react-youtube';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForwardStep } from '@fortawesome/pro-duotone-svg-icons/faForwardStep';
import { faBackwardStep } from '@fortawesome/pro-duotone-svg-icons/faBackwardStep';
import { faRepeat } from '@fortawesome/pro-duotone-svg-icons/faRepeat';

type YouTubePlayerProps = {
   videoId: string;
   youTubeTimestamps: [string, YouTubeTimestamp][];
};

type YouTubeTimestamp = {
   startTime: number;
   endTime: number;
};

type StepYouTubeTimestamp = {
   [stepNumber: number]: YouTubeTimestamp;
};

export default function YouTubePlayer({ videoId, youTubeTimestamps = [] }: YouTubePlayerProps) {
   const [currentTime, setCurrentTime] = useState<number>(0);
   const timestamps: StepYouTubeTimestamp = stepNumberTimestamps(youTubeTimestamps);
   const validStepNumbers: number[] = Object.keys(timestamps)
      .map(step => Number.parseInt(step))
      .sort((a, b) => a - b);
   const playerRef = useRef<YouTube | null>(null);
   const fragmentStepNumber = getStepNumberFromStepid(window.location.hash.slice(2));
   const [stepNumber, setStepNumber] = useState<number | null>(
      fragmentStepNumber && validStepNumbers.includes(fragmentStepNumber)
         ? fragmentStepNumber
         : null
   );
   const [loopCurrentStep, setLoopCurrentStep] = useState<boolean>(stepNumber ? true : false); // If the step number is set (only possible from URL fragment), loop it
   const [scrollTextWithVideo, setScrollTextWithVideo] = useState<boolean>(true);
   const [playerHeight, setPlayerHeight] = useState<number>(0);
   const handleClick = (event: any) => {
      const button = event.target;
      const stepNumber = Number.parseInt(button.dataset.stepNumber);
      if (playerRef.current && validStepNumbers.includes(stepNumber)) {
         setScrollTextWithVideo(false);
         setStepNumber(stepNumber);
         playerRef.current.internalPlayer.seekTo(timestamps[stepNumber].startTime);
      }
   };
   const divRef = useRef<HTMLDivElement>(null);
   const updateCenterTop = () => {
      if (!divRef.current || !divRef.current.offsetParent) return;
      const height = divRef.current.clientHeight;
      setPlayerHeight(height);
      if (isMobilePortrait()) {
         (divRef.current.offsetParent as HTMLElement).style.top = '1%';
      } else {
         (divRef.current.offsetParent as HTMLElement).style.top = `calc(50% - ${height / 2}px)`;
      }
   };

   useEffect(() => {
      updateCenterTop();
      highlightCurrentPlayingStep(validStepNumbers, stepNumber);
      window.addEventListener('resize', updateCenterTop);
      const player = playerRef.current?.internalPlayer;
      let intervalId: ReturnType<typeof setInterval> | undefined;

      // Setup skipToStepButtons on page
      const skipToStepButtons = Array.from(document.querySelectorAll('.skip-to-step'));
      skipToStepButtons.forEach(button => {
         button.addEventListener('click', handleClick);
      });

      if (loopCurrentStep) {
         // Loop the section of the video for the current step
         if (stepNumber === null) {
            scrollToStep(validStepNumbers[0], playerHeight);
            setStepNumber(validStepNumbers[0]);
         } else {
            const startTime = timestamps[stepNumber]?.startTime;
            const endTime = timestamps[stepNumber]?.endTime;
            intervalId = setInterval(() => {
               player.getCurrentTime().then((time: number) => {
                  if (time >= endTime || time < startTime) {
                     setCurrentTime(time);
                     player.seekTo(startTime);
                  }
               });
            }, 500);
         }
         if (scrollTextWithVideo && stepNumber) {
            scrollToStep(stepNumber, playerHeight);
         }
      } else if (scrollTextWithVideo) {
         // Scroll to next step whenever video leaves timestamp range
         intervalId = trackTimestamps(player, setStepNumber, setCurrentTime, youTubeTimestamps);
         player.getPlayerState().then(() => {
            if (stepNumber) {
               scrollToStep(stepNumber, playerHeight);
            }
         });
      } else {
         // Update the currently playing step number in the bottom bar
         intervalId = trackTimestamps(player, setStepNumber, setCurrentTime, youTubeTimestamps);
      }

      // Cleanup when the component unmounts
      return () => {
         clearInterval(intervalId);
         skipToStepButtons.forEach(button => {
            button.removeEventListener('click', handleClick);
         });
         window.removeEventListener('resize', updateCenterTop);
      };
   }, [loopCurrentStep, scrollTextWithVideo, stepNumber]);

   return (
      <div className="floating-youtube-player" ref={divRef}>
         <YouTube
            videoId={videoId}
            opts={{
               width: '100%',
               height: '100%',
            }}
            className={'floating-youtube-player-iframe'}
            ref={playerRef}
         />
         <Box className="floating-youtube-player-bottom-bar" backgroundColor="gray.200">
            <Flex className="youtube-bottom-bar-text">
               <Text
                  onClick={() => {
                     if (stepNumber) {
                        scrollToStep(stepNumber, playerHeight);
                     }
                  }}
                  color="brand.500"
                  cursor="pointer"
                  fontWeight="semibold"
                  position="absolute"
               >
                  {stepNumber ? `Step ${stepNumber}` : ''}
               </Text>
            </Flex>
            <div>
               <Button
                  className="bottom-bar-component scroll-with-video-button"
                  onClick={() => {
                     setScrollTextWithVideo(!scrollTextWithVideo);
                  }}
                  backgroundColor={scrollTextWithVideo ? 'brand.200' : 'white'}
                  title="Scroll the guide with video playback"
                  _hover={{}}
               >
                  {/* Scroll Text with Video */}
                  {_js('Auto-Scroll')}
               </Button>
               <Button
                  className="bottom-bar-component loop-current-step-button"
                  onClick={() => {
                     if (!loopCurrentStep && stepNumber) {
                        scrollToStep(stepNumber, playerHeight);
                     }
                     setLoopCurrentStep(!loopCurrentStep);
                  }}
                  backgroundColor={loopCurrentStep ? 'brand.200' : 'white'}
                  title="Loop the section of the video for the current step"
                  _hover={{}}
               >
                  {/* Loop Current Step */}
                  <FontAwesomeIcon icon={faRepeat} />
               </Button>
            </div>
            <div>
               <Button
                  className="bottom-bar-component previous-step-button"
                  isDisabled={
                     timestamps[validStepNumbers[0]]?.startTime > currentTime // If the video is before the first step
                  }
                  onClick={() => {
                     const prevStep: number =
                        stepNumber === null
                           ? getNearestPreviousStep(currentTime, timestamps)
                           : getPreviousTimestampedStep(stepNumber, validStepNumbers);
                     setStepNumber(prevStep);
                     if (scrollTextWithVideo) {
                        scrollToStep(prevStep, playerHeight);
                     }
                     if (!loopCurrentStep && playerRef.current) {
                        playerRef.current.internalPlayer.seekTo(timestamps[prevStep].startTime);
                     }
                  }}
                  title="Previous Step"
               >
                  <FontAwesomeIcon icon={faBackwardStep} />
               </Button>
               <Button
                  className="bottom-bar-component next-step-button"
                  isDisabled={
                     validStepNumbers.length === 0 ||
                     (stepNumber !== null &&
                        stepNumber >= (validStepNumbers.at(-1) ?? Number.MAX_VALUE)) // Get past err TS2532 for validStepNumbers.at(-1)
                  }
                  onClick={() => {
                     const nextStep: number | null =
                        stepNumber === null
                           ? getNearestNextStep(currentTime, timestamps)
                           : getNextTimestampedStep(stepNumber, validStepNumbers);
                     setStepNumber(nextStep);
                     if (scrollTextWithVideo) {
                        scrollToStep(nextStep, playerHeight);
                     }
                     if (!loopCurrentStep && playerRef.current) {
                        playerRef.current.internalPlayer.seekTo(timestamps[nextStep].startTime);
                     }
                  }}
                  title="Next Step"
               >
                  <FontAwesomeIcon icon={faForwardStep} />
               </Button>
            </div>
         </Box>
      </div>
   );
}

/**
 * Returns the step number from a step id. If the step number isn't found, return null.
 */
function getStepNumberFromStepid(stepid: string) {
   const element = document.querySelector(`#s${stepid}`);
   const stepNumber = element?.getAttribute('data-step-number');
   return stepNumber ? Number.parseInt(stepNumber, 10) : null;
}

/**
 * Returns an object with the step number as the key and the timestamp as the
 * value. Differs from youtubeTimestamps in that it uses the step number as the
 * key instead of the stepid.
 */
function stepNumberTimestamps(timestamps: [string, YouTubeTimestamp][]) {
   const stepNumberTimestamps: StepYouTubeTimestamp = {};
   timestamps.forEach(([stepid, timestamp]: [string, YouTubeTimestamp]) => {
      const stepNumber = getStepNumberFromStepid(stepid);
      if (stepNumber !== null) {
         stepNumberTimestamps[stepNumber] = timestamp;
      }
   });
   return stepNumberTimestamps;
}

/**
 * Scrolls to the step with the given step number, even if it doesn't have a
 * timestamp.
 */
function scrollToStep(stepNumber: number, playerHeight: number) {
   const metaElement = document.querySelector(`li.step[data-step-number="${stepNumber}"]`);
   if (metaElement) {
      if (isMobilePortrait()) {
         window.scrollTo({
            behavior: 'smooth',
            top: metaElement.getBoundingClientRect().top + window.pageYOffset - playerHeight,
         });
      } else {
         metaElement.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
      }
   }
}

/**
 * Tracks the timestamps of the video and updates the stepNumber.
 */
function trackTimestamps(
   player: any,
   setStepNumber: Dispatch<SetStateAction<number | null>>,
   setCurrentTime: Dispatch<SetStateAction<number>>,
   timestamps: [string, YouTubeTimestamp][]
) {
   return setInterval(() => {
      player.getCurrentTime().then((time: number) => {
         setCurrentTime(time);
         let found = false;
         timestamps.forEach(([stepid, timestamp]: [string, YouTubeTimestamp]) => {
            if (time >= timestamp.startTime && time < timestamp.endTime) {
               const stepNumber = getStepNumberFromStepid(stepid);
               setStepNumber(stepNumber);
               found = true;
            }
         });
         if (!found) {
            setStepNumber(null);
         }
      });
   }, 500);
}

function getNextTimestampedStep(stepNumber: number, validStepNumbers: number[]) {
   const index = validStepNumbers.indexOf(stepNumber);
   if (index === -1 || index === validStepNumbers.length - 1) {
      return stepNumber;
   }
   return validStepNumbers[index + 1];
}

function getPreviousTimestampedStep(stepNumber: number, validStepNumbers: number[]) {
   const index = validStepNumbers.indexOf(stepNumber);
   if (index === -1 || index === 0) {
      return stepNumber;
   }
   return validStepNumbers[index - 1];
}

function getNearestPreviousStep(time: number, timestamps: StepYouTubeTimestamp) {
   let nearestStep = 1;
   let nearestTime = Number.MIN_VALUE;
   Object.entries(timestamps).forEach(([step, timestamp]) => {
      if (timestamp.endTime < time && timestamp.endTime > nearestTime) {
         nearestTime = timestamp.endTime;
         nearestStep = Number.parseInt(step);
      }
   });
   return nearestStep;
}

function getNearestNextStep(time: number, timestamps: StepYouTubeTimestamp) {
   let nearestStep = 1;
   let nearestTime = Number.MAX_VALUE;
   Object.entries(timestamps).forEach(([step, timestamp]) => {
      if (timestamp.startTime > time && timestamp.startTime < nearestTime) {
         nearestTime = timestamp.startTime;
         nearestStep = Number.parseInt(step);
      }
   });
   return nearestStep;
}

function isMobilePortrait() {
   if (window.innerWidth < 768 && window.innerHeight > window.innerWidth) {
      return true;
   }
   return false;
}

function highlightCurrentPlayingStep(validStepNumbers: number[], stepNumber: number | null) {
   validStepNumbers.forEach(step => {
      const element = document.querySelector(`li.step[data-step-number="${step}"]`);
      element?.classList.toggle('youtube-active-step', step === stepNumber);
   });
}

initializeChakraComponent('YouTubePlayerComponent', YouTubePlayer);
